import { Component, HostListener, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StorageHelper } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/@common/helpers/storage.helper';
import { SmartAlertsApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/smart-alerts/smart-alerts-api';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';
import localeNl from '@angular/common/locales/nl';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeEs from '@angular/common/locales/es';
import { AppLoaderComponent } from './@commons/@components/loaders/app-loader/app-loader.component';
import { StorageService } from './@commons/@services/front/storage.service';
import { UserApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/user/user-api';
import { PosthogService } from './@commons/@services/front/posthog.service';
import { DeviceTokenService } from './@commons/@services/webview/device-token.service';
import { PremiumService } from './@commons/@services/front/premium.service';
import { PremiumCelebrationComponent } from './@commons/ui-components/premium-celebration/premium-celebration.component';

// Enregistre les langues nécessaires;

registerLocaleData(localeFr);
registerLocaleData(localeEn);
registerLocaleData(localeNl);
registerLocaleData(localeDe);
registerLocaleData(localeIt);
registerLocaleData(localeEs);

@Component({
  selector: 'vw-root',
  imports: [RouterOutlet, AppLoaderComponent, PremiumCelebrationComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  providers: [
    {
      provide: UserApi,
      useClass: UserApi,
    },
    {
      provide: SmartAlertsApi,
      useClass: SmartAlertsApi,
    },
  ],
})
export class AppComponent implements OnInit {
  lastTouchEnd = 0;

  private static readonly APP_OPEN_THROTTLE_MS = 30 * 60 * 1000; // 30 min

  /** Alert uids already attributed this session — avoids re-firing markAsOpened on re-navigations. */
  private readonly trackedOpenedAlerts = new Set<string>();

  readonly premiumService = inject(PremiumService);

  /**
   * Un essai n'est pas un achat : dire « merci pour ton abonnement » à qui vient
   * de cliquer « essayer » sonne faux, et laisse croire qu'il a payé. Les mois
   * offerts (`MANUAL`) relèvent de la même prudence.
   */
  isTrialActivation(): boolean {
    const provider = this.premiumService.subscription()?.provider;
    return provider === 'TRIAL' || provider === 'MANUAL';
  }

  constructor(
    private translate: TranslateService,
    private storageService: StorageService,
    private posthogService: PosthogService,
    private deviceTokenService: DeviceTokenService,
    private userApi: UserApi,
    private router: Router,
    private smartAlertsApi: SmartAlertsApi,
  ) {
    const availableLanguages = ['fr', 'en', 'nl', 'de', 'it', 'es'];
    this.translate.addLangs(availableLanguages);
    this.translate.setDefaultLang('fr');
    // Récupérer la langue stockée (si existante)
    const storedLang = this.storageService.getCurrentLanguage()?.toLowerCase();
    // Détecter la langue du navigateur
    const browserLang = navigator.language.split('-')[0]; // Ex: "en-US" → "en"

    // Vérifier si la langue stockée est valide, sinon utiliser celle du navigateur, sinon "fr"
    const userLang = availableLanguages.includes(storedLang)
      ? storedLang
      : availableLanguages.includes(browserLang)
        ? browserLang
        : 'fr';

    console.log(`Langue choisie : ${userLang}`);

    // Stocker la langue sélectionnée pour éviter de la redétecter à chaque fois
    this.storageService.setCurrentLang(userLang.toUpperCase());

    // Appliquer la langue choisie
    this.translate.use(userLang);
  }

  @HostListener('touchend', ['$event'])
  preventDoubleTapZoom(event: TouchEvent) {
    const now = new Date().getTime();
    if (now - this.lastTouchEnd <= 300) {
      // 300ms pour bloquer le zoom sur double tap
      event.preventDefault();
    }
    this.lastTouchEnd = now;
  }

  ngOnInit(): void {
    StorageHelper.getInstance()
      .getMobileStorageItem('access_token')
      .then((res) => {
        StorageHelper.getInstance().setItem('access_token', res || '');
        // Record the cold-start app open once the token has been hydrated from native storage.
        this.trackAppOpen();
        // L'etat premium ne peut etre lu qu'une fois le jeton hydrate depuis le
        // stockage natif : plus tot, on lirait « gratuit » pour un abonne.
        void this.premiumService.init();
        // A push tapped from a cold start lands us on a deep-link carrying ?alertUid=…
        // — attribute the OPEN now that the token is available.
        this.trackOpenedAlertFromUrl(this.router.url);
      });
    StorageHelper.getInstance()
      .getMobileStorageItem('refresh_token')
      .then((res) => {
        StorageHelper.getInstance().setItem('refresh_token', res || '');
      });

    // In-session navigations (push tapped while the app is already open) also carry the param.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) =>
        this.trackOpenedAlertFromUrl(event.urlAfterRedirects),
      );
  }

  /**
   * Reads the `alertUid` query param from a deep-link URL and records the push OPEN
   * (analytics). De-duplicated per session and fire-and-forget: a failure (e.g. the
   * token is not hydrated yet, or the user is anonymous) must never break navigation.
   */
  private trackOpenedAlertFromUrl(url: string): void {
    const queryIndex = url.indexOf('?');
    if (queryIndex === -1) {
      return;
    }
    const alertUid = new URLSearchParams(url.slice(queryIndex + 1)).get(
      'alertUid',
    );
    if (!alertUid || this.trackedOpenedAlerts.has(alertUid)) {
      return;
    }
    this.trackedOpenedAlerts.add(alertUid);
    this.smartAlertsApi.markAsOpened(alertUid).catch(() => {});
  }

  /**
   * Records an app open (cold start or foreground resume) for the logged-in user.
   * Throttled to 30 min so internal navigations / quick refreshes don't count.
   * Fire-and-forget: a failure must never break the UX.
   */
  private trackAppOpen(): void {
    const token = StorageHelper.getInstance().getItem('access_token');
    if (!token) {
      return; // anonymous / pre-login opens are out of scope
    }
    const last = Number(localStorage.getItem('last_app_open_tracked') || 0);
    if (Date.now() - last < AppComponent.APP_OPEN_THROTTLE_MS) {
      return;
    }
    localStorage.setItem('last_app_open_tracked', String(Date.now()));
    this.userApi.appOpen().catch(() => {});
    this.posthogService.capture('app_open');
  }

  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    // The Flutter WebView is reused across resumes, so visibilityState flips to
    // 'visible' on native foreground — this covers mobile without touching Flutter.
    if (document.visibilityState === 'visible') {
      this.trackAppOpen();
    }
  }
}
